import ReactECharts from 'echarts-for-react'
import { groupBy, maxBy, minBy, orderBy, round, countBy, take, keys, compact, meanBy } from 'lodash'
import PropTypes from 'prop-types'
import React, { useCallback, useMemo, useState } from 'react'
import { useSelector, shallowEqual } from 'react-redux'
import i18n from 'simple-react-i18n'
import { REMARK_CODES, searchP90Analysis } from 'utils/AnalyseUtils'
import { getBeginingOfTheYear, getEndOfTheYear, getYear } from 'utils/DateUtil'
import { getLabel } from 'utils/StoreUtils'
import { Grid, Tooltip, Dialog, DialogActions, DialogContent, Button } from '@mui/material'
import { calendarIcon, chartSymbol, exportExcelIcon, exportPictureIcon, fullScreenExitIcon, fullScreenIcon } from 'components/echart/EChartUtils'
import echarts from 'echarts/lib/echarts'
import { exportFile } from 'utils/ExportDataUtil'
import DtoAnalysisLight from 'quality/dto/analyse/DtoAnalysisLight'
import DtoAnalysisUltraLight from 'quality/dto/analyse/DtoAnalysisUltraLight'
import useListIndexed from 'utils/customHook/useListIndexed'
import { NewTable } from 'components/datatable/NewTable'
import { compareToThreshold, THRESHOLD_DISCRIMINANT } from 'utils/QualityThresholdUtils'
import { CheckBox, CheckBoxOutlineBlank, HelpRounded } from '@mui/icons-material'
import { beige, disabledBorderInputColor, IAEAU_SCALE_COLORS, darkBlue } from 'utils/constants/ColorTheme'
import { DefaultDialogTitle } from 'components/styled/Dialog'
import useBoolean from 'utils/customHook/useBoolean'
import { filterIcon } from 'components/echart/EChartUtils'
import { MIN, MAX, AVERAGE, P90, CATCHMENT_IRRELEVANT_PESTICIDES } from 'catchment/constants/CatchmentConstant'
import useApplicationSetting, { listStringParser } from 'utils/customHook/useApplicationSetting'
import { ParameterGraphModal } from 'quality/components/qualityComponents/ParameterGraph'
import useAccountSetting from 'utils/customHook/useAccountSetting'
import { SHOW_SANDRE_CODE } from 'account/constants/AccountSettingsConstants'
import NumberField from 'components/forms/NumberField'
import NewRadioButtons from 'components/forms/NewRadioButtons'
import { QualityThresholdType } from 'quality/shapes/QualityThresholdShapes'

const EXPORT_ALL_FREQUENCY_HEADERS = ['parameterCode', 'parameter', 'nbAnalysis', 'quantificationBelow', 'quantificationHigher', 'withoutQuantification', 'maxQuantified']
const EXPORT_NO_FREQUENCY_SANDRE_CODE = ['parameter', 'nbAnalysis', 'quantificationBelow', 'quantificationHigher', 'withoutQuantification', 'maxQuantified']

const ToolboxPeriodDialog = ({
    yearsPopin,
    closeYearsPopin,
    yearsValues,
    setYearsValues,
}) => {
    const [yearsFields, setYearsFields] = useState(yearsValues)

    return (
        <Dialog
            open={yearsPopin}
        >
            <DefaultDialogTitle
                title={i18n.referencePeriod}
                onClose={closeYearsPopin}
            />
            <DialogContent>
                <Grid container spacing={2}>
                    <Grid size={6}>
                        <NumberField
                            title={i18n.yearStart}
                            value={yearsFields.min}
                            onChange={v => setYearsFields(prev => ({ ...prev, min: v }))}
                            floatValue
                            onEnterKeyPress={v => {
                                setYearsFields(prev => ({ ...prev, min: v }))
                                setYearsValues({ min: v, max: yearsFields.max })
                                closeYearsPopin()
                            }}
                        />
                    </Grid>
                    <Grid size={6}>
                        <NumberField
                            title={i18n.yearEnd}
                            value={yearsFields.max}
                            onChange={v => setYearsFields(prev => ({ ...prev, max: v }))}
                            floatValue
                            onEnterKeyPress={v => {
                                setYearsFields(prev => ({ ...prev, max: v }))
                                setYearsValues({ min: yearsFields.min, max: v })
                                closeYearsPopin()
                            }}
                        />
                    </Grid>
                    <Grid size={3} offset={6}>
                        <Button
                            onClick={() => setYearsFields({ min: null, max: null })}
                            variant='outlined'
                            fullWidth
                        >
                            {i18n.clean}
                        </Button>
                    </Grid>
                    <Grid size={3}>
                        <Button
                            onClick={() => {
                                setYearsValues({ min: yearsFields.min, max: yearsFields.max })
                                closeYearsPopin()
                            }}
                            variant='contained'
                            fullWidth
                        >
                            {i18n.apply}
                        </Button>
                    </Grid>
                </Grid>
            </DialogContent>
        </Dialog>
    )
}

ToolboxPeriodDialog.propTypes = {
    yearsPopin: PropTypes.bool,
    closeYearsPopin: PropTypes.func,
    yearsValues: PropTypes.shape({
        min: PropTypes.number,
        max: PropTypes.number,
    }),
    setYearsValues: PropTypes.func,
}

const FrequencyPesticides = ({
    pesticidesAnalysis = [],
    uniqueThreshold = undefined,
    relevantThreshold = 0.1,
    irrelevantThreshold = 0.9,
    specificThresholds = [],
    pesticides = [],
    title = i18n.frequencyPesticideQuantification,
    qualitometersFiltered = [],
    startPeriod,
    endPeriod,
}) => {
    const {
        qualitometers,
        cities,
        parameters,
    } = useSelector(store => ({
        qualitometers: store.QualityReducer.qualitometersLight,
        cities: store.CityReducer.cities,
        parameters: store.ParameterReducer.parameters,
    }), shallowEqual)

    const {
        value: statisticsPopin,
        setTrue: openStatisticsPopin,
        setFalse: closeStatisticsPopin,
    } = useBoolean(false)
    const {
        value: stationsPopin,
        setTrue: openStationsPopin,
        setFalse: closeStationsPopin,
    } = useBoolean(false)
    const {
        value: yearsPopin,
        setTrue: openYearsPopin,
        setFalse: closeYearsPopin,
    } = useBoolean(false)
    const {
        value: isFullScreen,
        setFalse: closeFullScreen,
        toggle: toggleFullSCreen,
    } = useBoolean(false)
    const {
        value: isGraphOpen,
        setTrue: openGraph,
        setFalse: closeGraph,
    } = useBoolean(false)

    const thresholdsIndexed = useListIndexed(specificThresholds, 'parameterCode')

    const [modalData, setModalData] = useState('-1')
    const [stations, setStations] = useState([])
    const [yearsValues, setYearsValues] = useState({ min: getYear(startPeriod), max: getYear(endPeriod) })
    const [statChoice, setStatChoice] = useState(0)
    const [selectedLegend, setSelectedLegend] = useState({})

    const irrelevantPesticides = useApplicationSetting(CATCHMENT_IRRELEVANT_PESTICIDES, listStringParser)
    const generalShowSandreCode = useApplicationSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : true)
    const showSandreCode = useAccountSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : generalShowSandreCode)

    const statistics = useMemo(() => ([
        { value: 0, label: i18n.any },
        { value: MIN, label: i18n.min },
        { value: AVERAGE, label: i18n.average },
        { value: MAX, label: i18n.max },
        { value: P90, label: i18n.P90 },
    ]), [])

    const qualitoIds = useMemo(() => qualitometersFiltered.map(q => q.id), [qualitometersFiltered])
    const qualitometersSelected = useMemo(() => stations.length > 0 ? stations : qualitometersFiltered, [stations, qualitometersFiltered])
    const selectedIds = useMemo(() => qualitometersSelected.map(q => q.id), [qualitometersSelected])
    const stationsSelectedAnalysis = useMemo(() => selectedIds.length > 0 ? pesticidesAnalysis.filter(a => selectedIds.includes(a.qualitometer)) : pesticidesAnalysis, [pesticidesAnalysis, selectedIds])

    const usedStartPeriod = useMemo(() => getBeginingOfTheYear(yearsValues.min) || startPeriod || minBy(stationsSelectedAnalysis, 'sampleDate')?.sampleDate, [yearsValues.min, startPeriod, stationsSelectedAnalysis])
    const usedEndPeriod = useMemo(() => getEndOfTheYear(yearsValues.max) || endPeriod || maxBy(stationsSelectedAnalysis, 'sampleDate')?.sampleDate, [yearsValues.max, endPeriod, stationsSelectedAnalysis])
    const selectedAnalysis = useMemo(() => stationsSelectedAnalysis.filter(a => a.sampleDate >= usedStartPeriod && a.sampleDate <= usedEndPeriod), [stationsSelectedAnalysis, usedEndPeriod, usedStartPeriod])

    const countParam = useMemo(() => countBy(selectedAnalysis, 'parameter'), [selectedAnalysis])

    const createSeries = useCallback((list = [], color = '', name = '', parametersCount = {}, groupRemainingByParameter = {}) => {
        const groupByParameter = groupBy(list, 'parameter')
        const data = keys(groupByParameter).map(param => ({
            value: [param, groupByParameter[param].length * 100 / parametersCount[param], groupByParameter[param].length, groupRemainingByParameter[param]],
        }))
        return {
            type: 'bar',
            data,
            name,
            stack: true,
            barMaxWidth: 30,
            itemStyle: {
                color,
            },
        }
    }, [])

    const {
        without = [],
        higher = [],
        below = [],
    } = useMemo(() => groupBy(selectedAnalysis, ({ result, parameter, remark }) => {
        if (!remark || remark !== REMARK_CODES.VALID) {
            return 'without'
        }
        const defaultThreshold = uniqueThreshold ?? irrelevantPesticides.includes(parameter) ? irrelevantThreshold : relevantThreshold
        const th = thresholdsIndexed[parameter]?.threshold1 ?? defaultThreshold
        return compareToThreshold(result, th, THRESHOLD_DISCRIMINANT.SUP_OR_EQUAL) ? 'higher' : 'below'
    }), [selectedAnalysis, thresholdsIndexed, irrelevantPesticides, irrelevantThreshold, relevantThreshold, uniqueThreshold])

    const remainingHigher = useMemo(() => selectedLegend?.[`${i18n.quantificationHigher} ${i18n.threshold}`] === false ? [] : higher, [selectedLegend, higher])
    const remainingBelow = useMemo(() => selectedLegend?.[`${i18n.quantificationBelow} ${i18n.threshold}`] === false ? [] : below, [selectedLegend, below])
    const remainingWithout = useMemo(() => selectedLegend?.[i18n.withoutQuantification] === false ? [] : without, [selectedLegend, without])

    const groupRemainingByParameter = useMemo(() => groupBy([...remainingHigher, ...remainingBelow, ...remainingWithout], 'parameter'), [remainingHigher, remainingBelow, remainingWithout])
    const countRemainingParam = useMemo(() => countBy([...remainingHigher, ...remainingBelow, ...remainingWithout], 'parameter'), [remainingHigher, remainingBelow, remainingWithout])

    const completeSerieHigher = useMemo(() => createSeries(higher, IAEAU_SCALE_COLORS.MEDIOCRE, `${i18n.quantificationHigher} ${i18n.threshold}`, countParam), [createSeries, higher, countParam])
    const completeSerieBelow = useMemo(() => createSeries(below, beige, `${i18n.quantificationBelow} ${i18n.threshold}`, countParam), [createSeries, below, countParam])
    const completeSerieWithout = useMemo(() => createSeries(without, disabledBorderInputColor, i18n.withoutQuantification, countParam), [createSeries, without, countParam])

    const serieHigher = useMemo(() => createSeries(higher, IAEAU_SCALE_COLORS.MEDIOCRE, `${i18n.quantificationHigher} ${i18n.threshold}`, countRemainingParam, groupRemainingByParameter), [createSeries, higher, countRemainingParam, groupRemainingByParameter])
    const serieBelow = useMemo(() => createSeries(below, beige, `${i18n.quantificationBelow} ${i18n.threshold}`, countRemainingParam, groupRemainingByParameter), [createSeries, below, countRemainingParam, groupRemainingByParameter])
    const serieWithout = useMemo(() => createSeries(without, disabledBorderInputColor, i18n.withoutQuantification, countRemainingParam, groupRemainingByParameter), [createSeries, without, countRemainingParam, groupRemainingByParameter])

    const listParam = useMemo(() => pesticides.map(param => {
        const [, percentHigher = 0, nbHigher = 0] = completeSerieHigher.data.find(({ value }) => value[0] === param)?.value || []
        const [, percentBelow = 0, nbBelow = 0] = completeSerieBelow.data.find(({ value }) => value[0] === param)?.value || []
        const [, , nbWithout = 0] = completeSerieWithout.data.find(({ value }) => value[0] === param)?.value || []
        return {
            param,
            percentSum: percentHigher + percentBelow,
            percentHigher,
            nbTotal: nbHigher + nbBelow + nbWithout,
        }
    }).filter(p => p.percentSum), [pesticides, completeSerieBelow.data, completeSerieHigher.data, completeSerieWithout.data])

    const listParamOrdered = useMemo(() => orderBy(listParam, ['percentSum', 'percentHigher', 'nbTotal'], ['desc', 'desc', 'desc']).map(p => p.param), [listParam])

    const calculateStatistic = useCallback(() => {
        const formatAnalysis = [...remainingHigher, ...remainingBelow].map(a => ({ ...a, value: `${round(a.result, 5)}` }))
        const groupByParameter = groupBy(formatAnalysis, 'parameter')
        const statisticName = statistics.find(stat => stat.value === statChoice)?.label
        switch (statChoice) {
            case 1: return keys(groupByParameter).map(param => ({
                value: [param, minBy(groupByParameter[param], 'result')?.result],
                statistic: statisticName,
            }))
            case 2: return keys(groupByParameter).map(param => ({
                value: [param, maxBy(groupByParameter[param], 'result')?.result],
                statistic: statisticName,
            }))
            case 3: return keys(groupByParameter).map(param => ({
                value: [param, round(meanBy(groupByParameter[param], 'result'), 3)],
                statistic: statisticName,
            }))
            case 4: return keys(groupByParameter).map(param => {
                return {
                    value: [param, searchP90Analysis(groupByParameter[param])?.result],
                    statistic: statisticName,
                }
            })
            default: return []
        }
    }, [remainingHigher, remainingBelow, statChoice, statistics])

    const statisticSerie = useMemo(() => {
        const data = calculateStatistic()
        data.sort((a, b) => listParamOrdered.indexOf(a.value[0]) - listParamOrdered.indexOf(b.value[0]))
        return data.length > 0 ?
            {
                name: data[0].statistic,
                yAxisIndex: 1,
                type: 'line',
                data,
                itemStyle: {
                    color: darkBlue,
                },
            } : undefined
    }, [calculateStatistic, listParamOrdered])

    const series = useMemo(() => compact([
        serieBelow,
        serieHigher,
        serieWithout,
        statisticSerie,
    ]), [serieBelow, serieHigher, serieWithout, statisticSerie])

    const checkTitleHeight = (heightPercent) => {
        if (title.includes('\n')) return `${(heightPercent + 5).toString()}%`
        return `${heightPercent.toString()}%`
    }

    const options = {
        title: {
            text: title,
            left: 'center',
        },
        series,
        xAxis: [{
            type: 'category',
            data: take(listParamOrdered, 50),
            boundaryGap: true,
            showSplitLine: true,
            axisLabel: {
                formatter: v => {
                    const label = getLabel(parameters, v, 'name')
                    return label.length > 33 ? `${label.substring(0, 30)}...` : label
                },
                rotate: 50,
            },
        }],
        yAxis: [
            {
                type: 'value',
                name: `${i18n.frequency} (%)`,
                position: 'left',
                nameLocation: 'center',
                nameGap: 50,
                boundaryGap: true,
                min: 0,
                max: 100,
            },
            statisticSerie ? {
                type: 'value',
                name: `${statisticSerie.name} (µg/L)`,
                position: 'right',
                nameLocation: 'center',
                nameGap: 50,
                boundaryGap: true,
            } : undefined,
        ],
        legend: {
            top: checkTitleHeight(5),
            left: '10%',
            right: 180,
            type: 'scroll',
            selected: selectedLegend,
        },
        tooltip: {
            trigger: 'axis',
            axisPointer: {
                type: 'shadow',
                snap: true,
            },
            formatter: params => {
                const param = params[0].axisValue
                const [relevanceThreshold, relevance] = irrelevantPesticides.includes(param) ? [irrelevantThreshold, i18n.irrelevant] : [relevantThreshold, i18n.relevant]
                const [defaultThreshold, addingRelevance] = uniqueThreshold ? [uniqueThreshold, ''] : [relevanceThreshold, relevance]
                const thresholdUsed = thresholdsIndexed[param]?.threshold1 ?
                    `${i18n.threshold} : ${thresholdsIndexed[param]?.threshold1} µg/L (${i18n.qualitoPhysicoChimique})` :
                    `${i18n.threshold} : ${defaultThreshold} µg/L (${i18n.default}${addingRelevance ? ` ${addingRelevance}` : ''})`
                const paramName = getLabel(parameters, param)
                const barSeries = params.filter(s => s.seriesType === 'bar')
                const barTooltip = `${paramName}<br />${thresholdUsed}<br />${barSeries.map(({ seriesName, value: [, percent, result] }) => `${round(percent, 2)}% ${seriesName} (${result} ${result > 1 ? i18n.analysis : i18n.oneAnalysis}) `).join('<br />')}`
                const statisticData = params.find(s => s.seriesType === 'line')
                return statisticData ? `${barTooltip}<br />${statisticData.seriesName} : ${statisticData.value?.[1]} µg/L` : barTooltip
            },
        },
        grid: {
            top: checkTitleHeight(15),
            left: '6%',
            right: '3%',
            bottom: '80',
            containLabel: true,
            height: 400,
        },
        toolbox: {
            right: '3%',
            top: '5%',
            itemSize: 18,
            feature: {
                myToolStatistics: {
                    title: i18n.statistics,
                    icon: chartSymbol,
                    onclick: openStatisticsPopin,
                },
                myToolFilterYears: {
                    title: i18n.periode,
                    icon: calendarIcon,
                    onclick: openYearsPopin,
                },
                myToolFilterStations: {
                    show: qualitometersFiltered?.length > 0,
                    title: i18n.filterResult,
                    icon: filterIcon,
                    onclick: openStationsPopin,
                },
                myToolFullScreen: {
                    title: i18n.fullScreen,
                    icon: isFullScreen ? fullScreenExitIcon : fullScreenIcon,
                    onclick: toggleFullSCreen,
                },
                saveAsImage: {
                    title: i18n.export,
                    icon: exportPictureIcon,
                    name: i18n.frequencyPesticideQuantification,
                },
                myToolExport: {
                    title: i18n.excelExport,
                    icon: exportExcelIcon,
                    onclick: () => {
                        const groupByParam = groupBy(selectedAnalysis, 'parameter')
                        const exportData = keys(groupByParam).map(key => {
                            const analysis = groupByParam[key]
                            const {
                                countWithout = 0,
                                countHigher = 0,
                                countBelow = 0,
                            } = countBy(analysis, ({ result, remark, parameter }) => {
                                if (!remark || remark !== REMARK_CODES.VALID) {
                                    return 'countWithout'
                                }
                                const defaultThreshold = uniqueThreshold ?? irrelevantPesticides.includes(parameter) ? irrelevantThreshold : relevantThreshold
                                const th = thresholdsIndexed[parameter]?.threshold1 ?? defaultThreshold
                                return result > th ? 'countHigher' : 'countBelow'
                            })
                            const percentHigher = countHigher * 100 / countParam[key]
                            const percentBelow = countBelow * 100 / countParam[key]
                            const percentSum = percentHigher + percentBelow
                            const maxQuantified = maxBy(analysis.filter(a => a.remark === REMARK_CODES.VALID), 'result')?.result
                            return {
                                parameterCode: { value: key, cellType: 'right' },
                                parameter: getLabel(parameters, key, 'name'),
                                nbAnalysis: { value: countParam[key], format: '0', cellType: 'number' },
                                quantificationBelow: { value: `${round(percentBelow, 2)}%`, cellType: 'right' },
                                quantificationHigher: { value: `${round(percentHigher, 2)}%`, cellType: 'right' },
                                withoutQuantification: { value: `${round(countWithout * 100 / countParam[key], 2)}%`, cellType: 'right' },
                                maxQuantified: { value: maxQuantified, cellType: 'right', format: '0.0000' },
                                percentSum,
                                percentHigher,
                            }
                        })
                        const sortedData = orderBy(exportData, ['percentSum', 'percentHigher', 'nbAnalysis.value'], ['desc', 'desc', 'desc'])
                        exportFile({
                            data: exportData.length ? [
                                {
                                    ...sortedData[0],
                                    headers: showSandreCode ? EXPORT_ALL_FREQUENCY_HEADERS : EXPORT_NO_FREQUENCY_SANDRE_CODE,
                                    customHeaders: compact([
                                        showSandreCode ? 'parameterCode' : '',
                                        'parameter',
                                        'nbAnalysis',
                                        `${i18n.quantificationBelow} ${uniqueThreshold ?? i18n.threshold}`,
                                        `${i18n.quantificationHigher} ${uniqueThreshold ?? i18n.threshold}`,
                                        'withoutQuantification',
                                        'quantificationMax',
                                    ]),
                                },
                                ...sortedData.slice(1),
                            ] : [],
                            exportType: 'xlsx',
                            titleFile: i18n.frequencyPesticideQuantification,
                        }, true)
                    },
                },
            },
        },
    }

    const pointsFormated = useMemo(() => {
        return qualitometersFiltered.map(point => {
            const qualitometer = qualitometers.find(q => q.id === point.id)
            const qualitoAnalysis = pesticidesAnalysis.filter(a => a.qualitometer === point.id && a.sampleDate >= usedStartPeriod && a.sampleDate <= usedEndPeriod && a.remark === REMARK_CODES.VALID)
            const city = qualitometer && cities.find(c => c.code === qualitometer.townCode)
            const stationName = [qualitometer.code ? `[${qualitometer.code}]` : '', `${qualitometer.name || ''}`, `${city?.name ? `- ${city.name}` : ''}`].join(' ')
            return {
                catchmentPoint: { value: stationName },
                nbQuantifications: { value: qualitoAnalysis.length },
                id: point.id,
            }
        })
    }, [qualitometers, qualitometersFiltered, cities, pesticidesAnalysis, usedStartPeriod, usedEndPeriod])

    return (
        <>
            <ReactECharts
                echarts={echarts}
                option={options}
                notMerge={true}
                lazyUpdate={true}
                className={'row no-margin'}
                style={{ height: 500, paddingTop: 20 }}
                onEvents={ qualitoIds.length > 0 ?
                    {
                        click: (params) => {
                            setModalData(params)
                            openGraph()
                        },
                        legendselectchanged: legend => setSelectedLegend(legend.selected),
                    } : {
                        legendselectchanged: legend => setSelectedLegend(legend.selected),
                    }
                }
            />
            <ParameterGraphModal
                isOpen={isGraphOpen}
                closeGraph={closeGraph}
                analysis={modalData.value?.[3]}
                thresholds={[]}
                parameter={modalData.name}
            />
            <Dialog
                fullScreen
                open={isFullScreen}
            >
                <DialogContent>
                    <ReactECharts
                        echarts={echarts}
                        option={options}
                        notMerge={true}
                        lazyUpdate={true}
                        className={'row no-margin'}
                        style={{ height: '75vh', paddingTop: 20 }}
                        onEvents={ qualitoIds.length > 0 ?
                            {
                                click: (params) => {
                                    setModalData(params?.name)
                                    openGraph()
                                },
                                legendselectchanged: legend => setSelectedLegend(legend.selected),
                            } : {
                                legendselectchanged: legend => setSelectedLegend(legend.selected),
                            }
                        }
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeFullScreen} variant='contained'>
                        {i18n.close}
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog
                maxWidth='lg'
                open={stationsPopin}
            >
                <DefaultDialogTitle
                    title={
                        <Grid container columnSpacing={1}>
                            {i18n.selectSamplingPointsToAdd}
                            <Tooltip title={i18n.selectNoneOrAll}>
                                <HelpRounded />
                            </Tooltip>
                        </Grid>
                    }
                    onClose={closeStationsPopin}
                />
                <DialogContent>
                    <Grid container rowSpacing={0.5} columnSpacing={1}>
                        <Grid size={12}>
                            <NewTable
                                headers={[
                                    { key: 'catchmentPoint', value: (<span style={{ whiteSpace: 'pre-wrap' }}>{i18n.catchmentPoint}</span>) },
                                    { key: 'nbQuantifications', value: (<span style={{ whiteSpace: 'pre-wrap' }}>{`${i18n.nbQuantifications} (${getYear(usedStartPeriod)} - ${getYear(usedEndPeriod)})`}</span>) },
                                ]}
                                rows={pointsFormated}
                                lineActions={[{
                                    icon: (station) => stations.some(s => s.id === station.id) ? <CheckBox /> : <CheckBoxOutlineBlank />,
                                    onClick: (station) => setStations(prev => prev.some(s => s.id === station.id) ? prev.filter(s => s.id !== station.id) : [...prev, station]),
                                }]}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setStations([])} variant='outlined'>
                        {i18n.reinit}
                    </Button>
                    <Button onClick={closeStationsPopin} variant='contained'>
                        {i18n.validate}
                    </Button>
                </DialogActions>
            </Dialog>
            <ToolboxPeriodDialog
                yearsPopin={yearsPopin}
                closeYearsPopin={closeYearsPopin}
                yearsValues={yearsValues}
                setYearsValues={setYearsValues}
            />
            <Dialog
                open={statisticsPopin}
            >
                <DefaultDialogTitle
                    title={i18n.displayStatistic}
                    onClose={closeStatisticsPopin}
                />
                <DialogContent>
                    <Grid container alignItems={'flex-end'}>
                        <Grid size={6} offset={1}>
                            <NewRadioButtons
                                value={statChoice}
                                items={statistics}
                                onChange={v => setStatChoice(v)}
                                row={false}
                            />
                        </Grid>
                        <Grid size='auto' offset={'auto'}>
                            <Button onClick={closeStatisticsPopin} variant='contained'>
                                {i18n.apply}
                            </Button>
                        </Grid>
                    </Grid>
                </DialogContent>
            </Dialog>
        </>
    )
}

FrequencyPesticides.propTypes = {
    pesticidesAnalysis: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.instanceOf(DtoAnalysisLight)),
        PropTypes.arrayOf(PropTypes.instanceOf(DtoAnalysisUltraLight)),
    ]),
    qualitometersFiltered: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
    })),
    uniqueThreshold: PropTypes.number,
    relevantThreshold: PropTypes.number,
    irrelevantThreshold: PropTypes.number,
    specificThresholds: PropTypes.arrayOf(QualityThresholdType),
    pesticides: PropTypes.arrayOf(PropTypes.string),
    title: PropTypes.string,
    startPeriod: PropTypes.number,
    endPeriod: PropTypes.number,
}

export default FrequencyPesticides